import { addComponent, addEntity, removeEntity, hasComponent, query } from 'bitecs';
import { Config } from '@src/core/Config';
import { difficulty } from '@src/core/Difficulty';
import { PoolKind } from '@src/core/Pools';
import type { Rng } from '@src/core/Rng';
import {
  Anim,
  BOSS_ORBS,
  BossTag,
  ClipId,
  Damage,
  DragonTag,
  Dying,
  DYING_ANIM,
  DYING_REAP,
  Enemy,
  EnemyKind,
  FxTag,
  Health,
  Knockback,
  Lifetime,
  Mine,
  MINE_IDLE,
  OFFHAND_NONE,
  OFFHAND_SHIELD,
  Orbiter,
  Pickup,
  PickupKind,
  Pierce,
  Player,
  Position,
  PrevPosition,
  Projectile,
  RECENT_HITS,
  Radius,
  Spawner,
  Sprite,
  SpriteId,
  Team,
  TeamTag,
  Velocity,
  Weapon,
} from '@src/ecs/components';
import type { GameContext } from '@src/ecs/world';
import { Anims } from '@src/game/AnimDefs';
import { bossDef, bossTuning } from '@src/game/BossDefs';
import { floorMod } from '@src/game/FloorModifiers';
import { WeaponId, weaponDef } from '@src/game/weapons/WeaponDefs';

export const NO_ENTITY = -1;

function baseBody(ctx: GameContext, x: number, y: number, r: number, sprite: SpriteId, scale: number, tint: number, z: number): number {
  const eid = addEntity(ctx.world);
  addComponent(ctx.world, eid, Position);
  addComponent(ctx.world, eid, PrevPosition);
  addComponent(ctx.world, eid, Velocity);
  addComponent(ctx.world, eid, Radius);
  addComponent(ctx.world, eid, Sprite);
  addComponent(ctx.world, eid, Anim);
  addComponent(ctx.world, eid, Dying);
  Position.x[eid] = x;
  Position.y[eid] = y;
  PrevPosition.x[eid] = x;
  PrevPosition.y[eid] = y;
  Velocity.x[eid] = 0;
  Velocity.y[eid] = 0;
  Radius.r[eid] = r;
  Sprite.id[eid] = sprite;
  Sprite.scale[eid] = scale;
  Sprite.rot[eid] = 0;
  Sprite.tint[eid] = tint;
  Sprite.alpha[eid] = 1;
  Sprite.z[eid] = z;
  Sprite.squash[eid] = 0;
  Sprite.pop[eid] = 0;
  Sprite.face[eid] = 1;
  Sprite.flash[eid] = 0;
  Sprite.joltX[eid] = 0;
  Sprite.joltY[eid] = 0;
  Anim.clip[eid] = ClipId.Idle;
  Anim.frame[eid] = 0;
  Anim.time[eid] = ctx.rng.next() * 4; // desync loops across a swarm
  Anim.speed[eid] = 1;
  Anim.done[eid] = 0;
  Anim.lock[eid] = 0;
  Dying.flag[eid] = 0;
  return eid;
}

/** Switch clips, restarting playback. No-op if already on that clip unless `restart`. */
export function playClip(eid: number, clip: ClipId, restart = false, lock = false): void {
  if (Anim.clip[eid] !== clip || restart) {
    Anim.clip[eid] = clip;
    Anim.time[eid] = 0;
    Anim.frame[eid] = 0;
    Anim.done[eid] = 0;
  }
  Anim.lock[eid] = lock ? 1 : 0;
}

export function spawnPlayer(ctx: GameContext, inputIndex: number, x: number, y: number): number {
  const eid = baseBody(ctx, x, y, Config.player.radius, SpriteId.Player, Config.player.spriteScale, 0xffffff, 0.5);
  addComponent(ctx.world, eid, Health);
  addComponent(ctx.world, eid, TeamTag);
  addComponent(ctx.world, eid, Player);
  addComponent(ctx.world, eid, Weapon);
  Health.hp[eid] = Config.player.maxHealth;
  Health.max[eid] = Config.player.maxHealth;
  Health.invuln[eid] = 0;
  TeamTag.team[eid] = Team.Player;
  Player.inputIndex[eid] = inputIndex;
  Player.aimX[eid] = 1;
  Player.aimY[eid] = 0;
  Player.firing[eid] = 0;
  Player.aiming[eid] = 0;
  Player.moveX[eid] = 0;
  Player.moveY[eid] = 0;
  Player.score[eid] = 0;
  Player.cornerTime[eid] = 0;
  Player.idleTime[eid] = 0;
  Player.dead[eid] = 0;
  Player.recoil[eid] = 0;
  Player.offhand[eid] = OFFHAND_NONE;
  Player.offhandTime[eid] = 0;
  Player.swiftTime[eid] = 0;
  Player.dashTime[eid] = 0;
  Player.dashX[eid] = 0;
  Player.dashY[eid] = 0;
  giveWeapon(eid, WeaponId.Wand);
  return eid;
}

export function giveWeapon(eid: number, id: WeaponId): void {
  const prev = Weapon.typeId[eid];
  const def = weaponDef(id);
  Weapon.typeId[eid] = id;
  Weapon.timeLeft[eid] = def.duration;
  Weapon.overcharge[eid] = 0;
  Weapon.cooldown[eid] = 0;
  Weapon.pulse[eid] = 0;
  // Fresh weapon: a quick pop + flash so the swap reads on the body, not only the HUD.
  if (prev !== id) {
    Sprite.pop[eid] = Math.max(Sprite.pop[eid], 0.55);
    Sprite.flash[eid] = Math.max(Sprite.flash[eid], 0.85);
  }
}

function enemyBody(ctx: GameContext, kind: EnemyKind, x: number, y: number, radius: number, sprite: SpriteId, scale: number, hp: number, z: number): number {
  const eid = baseBody(ctx, x, y, radius, sprite, scale, 0xffffff, z);
  addComponent(ctx.world, eid, Health);
  addComponent(ctx.world, eid, TeamTag);
  addComponent(ctx.world, eid, Enemy);
  const boss = kind === EnemyKind.Boss;
  // Floor rules thin or thicken every non-boss body (wall guns get their own scale too).
  const rule = floorMod(ctx.floor.modifier);
  const ruleHp = boss ? 1 : rule.enemyHp * (isWallKind(kind) ? rule.wallGunHp : 1);
  hp = Math.max(1, Math.round(hp * (boss ? difficulty().bossHp : difficulty().enemyHp) * ruleHp));
  Health.hp[eid] = hp;
  Health.max[eid] = hp;
  Health.invuln[eid] = 0;
  TeamTag.team[eid] = Team.Enemy;
  Enemy.kind[eid] = kind;
  Enemy.stagger[eid] = 0;
  Enemy.seed[eid] = ctx.rng.next();
  Enemy.beat[eid] = ctx.rng.next() * 0.5;
  Enemy.mode[eid] = 0;
  Enemy.lockX[eid] = 0;
  Enemy.lockY[eid] = 0;
  Sprite.pop[eid] = 1;
  return eid;
}

/** Planted wall guns (gargoyle family). Mirrors WallEnemies.isWallEnemy without the import cycle. */
function isWallKind(kind: EnemyKind): boolean {
  return kind === EnemyKind.Gargoyle || kind === EnemyKind.Spitter;
}

/** Tier HP ramp shared by the guests that scale (imps, slimes, blights, hexers, mimics). */
function tierHp(ctx: GameContext, base: number, perTier = 0.15): number {
  return base * (1 + (ctx.room.tier - 1) * perTier);
}

export function spawnImp(ctx: GameContext, x: number, y: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Imp)) return NO_ENTITY;
  const c = Config.enemies.imp;
  const eid = enemyBody(ctx, EnemyKind.Imp, x, y, c.radius, SpriteId.Imp, 0.9, c.hp * (1 + (ctx.room.tier - 1) * 0.15), 0.3);
  Sprite.squash[eid] = -0.6;
  Enemy.beat[eid] = 0.18 + ctx.rng.next() * 0.7;
  return eid;
}

export function spawnGolem(ctx: GameContext, x: number, y: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Golem)) return NO_ENTITY;
  const c = Config.enemies.golem;
  const eid = enemyBody(ctx, EnemyKind.Golem, x, y, c.radius, SpriteId.Golem, 1.9, c.hp * (1 + (ctx.room.tier - 1) * 0.2), 0.35);
  Sprite.squash[eid] = -0.5;
  Enemy.beat[eid] = 0.7 + ctx.rng.next() * 0.9;
  return eid;
}

/** Small fry share the imp pool so the swarm cap stays one number. */
export function spawnSkull(ctx: GameContext, x: number, y: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Imp)) return NO_ENTITY;
  const c = Config.enemies.skull;
  const eid = enemyBody(ctx, EnemyKind.Skull, x, y, c.radius, SpriteId.Skull, 0.95, c.hp, 0.32);
  Sprite.pop[eid] = 1.2;
  Enemy.beat[eid] = c.restSeconds * ctx.rng.next();
  return eid;
}

export function spawnSlime(ctx: GameContext, x: number, y: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Imp)) return NO_ENTITY;
  const c = Config.enemies.slime;
  const eid = enemyBody(ctx, EnemyKind.Slime, x, y, c.radius, SpriteId.Slime, 1.05, c.hp * (1 + (ctx.room.tier - 1) * 0.15), 0.29);
  Sprite.squash[eid] = 0.7;
  // Phase the first squat so a pack does not hop on one beat.
  Enemy.beat[eid] = c.squatSeconds * (0.08 + ctx.rng.next() * 0.92);
  return eid;
}

export function spawnBat(ctx: GameContext, x: number, y: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Imp)) return NO_ENTITY;
  const c = Config.enemies.bat;
  const eid = enemyBody(ctx, EnemyKind.Bat, x, y, c.radius, SpriteId.Bat, 0.95, c.hp, 0.34);
  Enemy.beat[eid] = c.orbitSeconds * (0.2 + ctx.rng.next() * 0.95);
  return eid;
}

/** Planted wall gun. `hpScale` is 1 in combat/hazard, lower in treasure vaults. */
export function spawnGargoyle(ctx: GameContext, x: number, y: number, hpScale = 1): number {
  if (!ctx.pools.tryAcquire(PoolKind.Gargoyle)) return NO_ENTITY;
  const c = Config.enemies.gargoyle;
  const hp = c.hp * (1 + (ctx.room.tier - 1) * 0.12) * hpScale;
  const eid = enemyBody(ctx, EnemyKind.Gargoyle, x, y, c.radius, SpriteId.Gargoyle, 1.35, hp, 0.22);
  Sprite.tint[eid] = 0xd2c4a8;
  Enemy.beat[eid] = c.fireInterval * (0.35 + ctx.rng.next() * 0.5);
  Enemy.mode[eid] = 0;
  if (Math.abs(x) > Math.abs(y)) Sprite.face[eid] = x > 0 ? -1 : 1;
  return eid;
}

/** Acid spitter: gargoyle mount, three-bolt fan. Shares the wall-gun pool. */
export function spawnSpitter(ctx: GameContext, x: number, y: number, hpScale = 1): number {
  if (!ctx.pools.tryAcquire(PoolKind.Gargoyle)) return NO_ENTITY;
  const c = Config.enemies.spitter;
  const hp = c.hp * (1 + (ctx.room.tier - 1) * 0.12) * hpScale;
  const eid = enemyBody(ctx, EnemyKind.Spitter, x, y, c.radius, SpriteId.Spitter, 1.35, hp, 0.22);
  Sprite.tint[eid] = 0x9ee08a;
  Enemy.beat[eid] = c.fireInterval * (0.45 + ctx.rng.next() * 0.5);
  Enemy.mode[eid] = 0;
  if (Math.abs(x) > Math.abs(y)) Sprite.face[eid] = x > 0 ? -1 : 1;
  return eid;
}

/** Hollow wraith: shares the small-fry pool. Starts mid-drift so a pack does not fade on one beat. */
export function spawnWraith(ctx: GameContext, x: number, y: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Imp)) return NO_ENTITY;
  const c = Config.enemies.wraith;
  const eid = enemyBody(ctx, EnemyKind.Wraith, x, y, c.radius, SpriteId.Wraith, 1.15, c.hp, 0.33);
  Sprite.tint[eid] = 0xb4c8ff;
  Sprite.alpha[eid] = 0.9;
  Enemy.beat[eid] = c.driftSeconds * (0.35 + ctx.rng.next() * 0.65);
  return eid;
}

/** Imp hexer: the kiting caster. Shares the small-fry pool. */
export function spawnHexer(ctx: GameContext, x: number, y: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Imp)) return NO_ENTITY;
  const c = Config.enemies.hexer;
  const eid = enemyBody(ctx, EnemyKind.Hexer, x, y, c.radius, SpriteId.Hexer, 1.0, tierHp(ctx, c.hp), 0.3);
  Sprite.tint[eid] = 0x7ff0ff;
  Sprite.squash[eid] = -0.5;
  Enemy.beat[eid] = c.castInterval * (0.4 + ctx.rng.next() * 0.6);
  return eid;
}

/** Cinder skull: the walking bomb. Shares the small-fry pool. */
export function spawnCinder(ctx: GameContext, x: number, y: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Imp)) return NO_ENTITY;
  const c = Config.enemies.cinder;
  const eid = enemyBody(ctx, EnemyKind.Cinder, x, y, c.radius, SpriteId.Cinder, 1.0, c.hp, 0.32);
  Sprite.tint[eid] = 0xffa050;
  Sprite.pop[eid] = 1.1;
  // beat counts up: it is the chase timer that drives the speed ramp.
  Enemy.beat[eid] = 0;
  return eid;
}

/** Blight slime: the splitter. Shares the small-fry pool. */
export function spawnBlight(ctx: GameContext, x: number, y: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Imp)) return NO_ENTITY;
  const c = Config.enemies.blight;
  const eid = enemyBody(ctx, EnemyKind.Blight, x, y, c.radius, SpriteId.Blight, 1.5, tierHp(ctx, c.hp), 0.28);
  Sprite.tint[eid] = 0xc884ff;
  Sprite.squash[eid] = 0.7;
  Enemy.beat[eid] = c.squatSeconds * (0.1 + ctx.rng.next() * 0.9);
  return eid;
}

/** Ember brute: shares the heavy pool with golems. */
export function spawnBrute(ctx: GameContext, x: number, y: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Golem)) return NO_ENTITY;
  const c = Config.enemies.brute;
  const eid = enemyBody(ctx, EnemyKind.Brute, x, y, c.radius, SpriteId.Brute, 1.55, tierHp(ctx, c.hp, 0.18), 0.35);
  Sprite.tint[eid] = 0xff8c66;
  Sprite.squash[eid] = -0.5;
  Enemy.beat[eid] = c.chargeRest * (0.6 + ctx.rng.next() * 0.6);
  return eid;
}

/** Mimic: sits dormant as a gold chest until woken. Shares the small-fry pool. */
export function spawnMimic(ctx: GameContext, x: number, y: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Imp)) return NO_ENTITY;
  const c = Config.enemies.mimic;
  const eid = enemyBody(ctx, EnemyKind.Mimic, x, y, c.radius, SpriteId.Mimic, 1.25, tierHp(ctx, c.hp), 0.2);
  // Same gold as the real prize so the tell is behaviour, not colour.
  Sprite.tint[eid] = 0xffd94a;
  Sprite.pop[eid] = 0.6;
  Enemy.beat[eid] = 0;
  Enemy.mode[eid] = 0;
  return eid;
}

/**
 * Floor boss. An Enemy (so collision, damage and the room-clear count treat it as
 * one) plus a BossTag that BossSystem drives. Arrives invulnerable and invisible;
 * BossSystem fades it in over the entrance ritual.
 */
export function spawnBoss(ctx: GameContext, kind: number, x: number, y: number, hpMult = 1): number {
  if (!ctx.pools.tryAcquire(PoolKind.Boss)) return NO_ENTITY;
  const c = Config.boss;
  const def = bossDef(kind);
  const tuning = bossTuning(kind, ctx.floor.floor, ctx.room.roomIndex, hpMult);
  const eid = enemyBody(ctx, EnemyKind.Boss, x, y, def.radius, def.sprite, def.scale, tuning.hp, 0.6);
  addComponent(ctx.world, eid, BossTag);
  Sprite.tint[eid] = def.spriteTint;
  Health.invuln[eid] = tuning.arriveSeconds + 0.1;
  BossTag.kind[eid] = kind;
  BossTag.room[eid] = ctx.room.roomIndex;
  BossTag.hpMult[eid] = hpMult;
  BossTag.lockX[eid] = 0;
  BossTag.lockY[eid] = 0;
  BossTag.splits[eid] = 0;
  BossTag.enraged[eid] = 0;
  BossTag.phase[eid] = 0;
  BossTag.phaseTime[eid] = 0;
  BossTag.pattern[eid] = 0;
  BossTag.step[eid] = 0;
  BossTag.stepTime[eid] = 0;
  BossTag.rest[eid] = tuning.rest;
  BossTag.casts[eid] = 0;
  BossTag.angle[eid] = ctx.rng.next() * Math.PI * 2;
  BossTag.targetX[eid] = x;
  BossTag.targetY[eid] = y;
  BossTag.blinkCooldown[eid] = c.blinkCooldown * 0.5;
  BossTag.halfCalled[eid] = 0;
  BossTag.bob[eid] = ctx.rng.next() * Math.PI * 2;
  BossTag.orbs.fill(-1, eid * BOSS_ORBS, eid * BOSS_ORBS + BOSS_ORBS);
  BossTag.orbCount[eid] = 0;
  BossTag.burstAt[eid] = 0;
  Sprite.alpha[eid] = 0;
  Sprite.pop[eid] = 0;
  return eid;
}

/** Score value per enemy kind, before tier and combo multipliers. */
export function enemyScore(kind: EnemyKind): number {
  const e = Config.enemies;
  switch (kind) {
    case EnemyKind.Boss:
      return Config.boss.score;
    case EnemyKind.Golem:
      return e.golem.score;
    case EnemyKind.Skull:
      return e.skull.score;
    case EnemyKind.Slime:
      return e.slime.score;
    case EnemyKind.Bat:
      return e.bat.score;
    case EnemyKind.Gargoyle:
      return e.gargoyle.score;
    case EnemyKind.Spitter:
      return e.spitter.score;
    case EnemyKind.Wraith:
      return e.wraith.score;
    case EnemyKind.Hexer:
      return e.hexer.score;
    case EnemyKind.Cinder:
      return e.cinder.score;
    case EnemyKind.Blight:
      return e.blight.score;
    case EnemyKind.Brute:
      return e.brute.score;
    case EnemyKind.Mimic:
      return e.mimic.score;
    case EnemyKind.Imp:
    default:
      return e.imp.score;
  }
}

/** Pool an enemy kind was drawn from (small fry share one cap so the swarm stays one number). */
export function enemyPool(kind: EnemyKind): PoolKind {
  switch (kind) {
    case EnemyKind.Golem:
    case EnemyKind.Brute:
      return PoolKind.Golem;
    case EnemyKind.Gargoyle:
    case EnemyKind.Spitter:
      return PoolKind.Gargoyle;
    case EnemyKind.Boss:
      return PoolKind.Boss;
    default:
      return PoolKind.Imp;
  }
}

export function spawnSpawner(ctx: GameContext, x: number, y: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Spawner)) return NO_ENTITY;
  const c = Config.enemies.spawner;
  const eid = baseBody(ctx, x, y, c.radius, SpriteId.Portal, 3.05, 0xffffff, 0.1);
  addComponent(ctx.world, eid, Health);
  addComponent(ctx.world, eid, TeamTag);
  addComponent(ctx.world, eid, Spawner);
  Health.hp[eid] = Math.round(c.hp * (1 + (ctx.room.tier - 1) * 0.25) * difficulty().enemyHp);
  Health.max[eid] = Health.hp[eid];
  Health.invuln[eid] = 0;
  TeamTag.team[eid] = Team.Enemy;
  Spawner.timer[eid] = ctx.rng.range(0.2, 0.8);
  Spawner.spawned[eid] = 0;
  Spawner.interval[eid] = Math.max(c.minInterval, c.baseInterval / (1 + (ctx.room.tier - 1) * 0.18));
  Sprite.pop[eid] = 1;
  ctx.room.spawnersAlive++;
  return eid;
}

export function spawnDragon(ctx: GameContext, x: number, y: number): number {
  const c = Config.dragon;
  const eid = baseBody(ctx, x, y, c.radius, SpriteId.Dragon, c.scale, 0xffffff, 0.9);
  addComponent(ctx.world, eid, TeamTag);
  addComponent(ctx.world, eid, DragonTag);
  TeamTag.team[eid] = Team.Enemy;
  DragonTag.speed[eid] = c.baseSpeed;
  DragonTag.breathTimer[eid] = c.breathInterval + c.firstBreathDelay;
  DragonTag.phase[eid] = 0;
  DragonTag.phaseTime[eid] = 0;
  DragonTag.bob[eid] = 0;
  DragonTag.breathed[eid] = 0;
  DragonTag.jet[eid] = -1;
  Sprite.alpha[eid] = 0;
  return eid;
}

export interface ProjectileSpec {
  owner: number;
  weapon: WeaponId;
  sprite: SpriteId;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  life: number;
  damage: number;
  spawnerMult: number;
  knockback: number;
  pierce: number;
  tint: number;
  scale: number;
  /** Radius growth per second (shockwaves). */
  grow: number;
  /** Team.Player by default; the dragon's flames are Team.Enemy. */
  team?: Team;
}

export function spawnProjectile(ctx: GameContext, s: ProjectileSpec): number {
  if (!ctx.pools.tryAcquire(PoolKind.Projectile)) return NO_ENTITY;
  const eid = baseBody(ctx, s.x, s.y, s.radius, s.sprite, s.scale, s.tint, 0.7);
  addComponent(ctx.world, eid, TeamTag);
  addComponent(ctx.world, eid, Projectile);
  addComponent(ctx.world, eid, Lifetime);
  addComponent(ctx.world, eid, Damage);
  addComponent(ctx.world, eid, Pierce);
  addComponent(ctx.world, eid, Knockback);
  Velocity.x[eid] = s.vx;
  Velocity.y[eid] = s.vy;
  Sprite.rot[eid] = Math.atan2(s.vy, s.vx);
  TeamTag.team[eid] = s.team ?? Team.Player;
  Projectile.owner[eid] = s.owner;
  Projectile.weapon[eid] = s.weapon;
  Projectile.grow[eid] = s.grow;
  Projectile.recentCount[eid] = 0;
  Projectile.recent.fill(-1, eid * RECENT_HITS, eid * RECENT_HITS + RECENT_HITS);
  Lifetime.t[eid] = s.life;
  Damage.amount[eid] = s.damage;
  Damage.spawnerMult[eid] = s.spawnerMult;
  Pierce.left[eid] = s.pierce;
  Knockback.force[eid] = s.knockback;
  return eid;
}

/**
 * Pure visual one-shot (muzzle flash, explosion, slash arc). Lives exactly one
 * pass of its Idle clip, then reaps itself. Returns -1 when the FX pool is full.
 */
export function spawnFx(ctx: GameContext, sprite: SpriteId, x: number, y: number, rot: number, scale: number, tint = 0xffffff, z = 0.8): number {
  if (!ctx.pools.tryAcquire(PoolKind.Fx)) return NO_ENTITY;
  const eid = baseBody(ctx, x, y, 0, sprite, scale, tint, z);
  addComponent(ctx.world, eid, FxTag);
  addComponent(ctx.world, eid, Lifetime);
  FxTag.flag[eid] = 1;
  Sprite.rot[eid] = rot;
  Anim.time[eid] = 0;
  Lifetime.t[eid] = Anims.duration(sprite, ClipId.Idle);
  return eid;
}

export function spawnMine(ctx: GameContext, x: number, y: number): number {
  const c = Config.mines;
  const eid = baseBody(ctx, x, y, c.radius, SpriteId.Mine, 1.15, 0xffffff, 0.12);
  addComponent(ctx.world, eid, Mine);
  Mine.state[eid] = MINE_IDLE;
  Mine.fuse[eid] = 0;
  return eid;
}

export function spawnWeaponPickup(ctx: GameContext, x: number, y: number, weapon: WeaponId): number {
  if (!ctx.pools.tryAcquire(PoolKind.Pickup)) return NO_ENTITY;
  const def = weaponDef(weapon);
  const tint = def.id === WeaponId.Cataclysm ? 0xffffff : def.tint;
  const eid = baseBody(ctx, x, y, Config.pickups.weaponRadius, def.pickupSprite, 1.4, tint, 0.2);
  addComponent(ctx.world, eid, Pickup);
  addComponent(ctx.world, eid, Lifetime);
  Pickup.kind[eid] = PickupKind.Weapon;
  Pickup.sub[eid] = weapon;
  Pickup.drift[eid] = 0;
  Lifetime.t[eid] = Config.pickups.weaponLife;
  Sprite.pop[eid] = 1;
  return eid;
}

export function spawnHeart(ctx: GameContext, x: number, y: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Pickup)) return NO_ENTITY;
  const eid = baseBody(ctx, x, y, Config.pickups.weaponRadius * 0.9, SpriteId.PickupHeart, 1.2, 0xffffff, 0.2);
  addComponent(ctx.world, eid, Pickup);
  addComponent(ctx.world, eid, Lifetime);
  Pickup.kind[eid] = PickupKind.Heart;
  Pickup.sub[eid] = Math.round(Config.pickups.heartHeal * difficulty().heal);
  Pickup.drift[eid] = 0;
  Lifetime.t[eid] = Config.pickups.heartLife;
  Sprite.pop[eid] = 1;
  return eid;
}

export function spawnGem(ctx: GameContext, x: number, y: number, value: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Pickup)) return NO_ENTITY;
  const eid = baseBody(ctx, x, y, Config.pickups.gemRadius, SpriteId.Gem, 0.7, 0x5cffb0, 0.2);
  addComponent(ctx.world, eid, Pickup);
  addComponent(ctx.world, eid, Lifetime);
  Pickup.kind[eid] = PickupKind.Gem;
  Pickup.sub[eid] = Math.min(255, value);
  Pickup.drift[eid] = 0;
  Lifetime.t[eid] = Config.pickups.gemLife;
  Velocity.x[eid] = ctx.rng.range(-3, 3);
  Velocity.y[eid] = ctx.rng.range(-3, 3);
  return eid;
}

export enum PrizeKind {
  Cabbage = 0,
  Hat = 1,
  Chalice = 2,
  GoldChest = 3,
  Spellbook = 4,
  CrystalBall = 5,
  Potion = 6,
  Hourglass = 7,
}

export const PRIZE_KIND_COUNT = 8;

const PRIZE_SPRITES: readonly SpriteId[] = [
  SpriteId.PrizeCabbage,
  SpriteId.PrizeHat,
  SpriteId.PrizeChalice,
  SpriteId.PrizeGoldChest,
  SpriteId.PrizeSpellbook,
  SpriteId.PrizeCrystalBall,
  SpriteId.PrizePotion,
  SpriteId.PrizeHourglass,
];

export function randomPrizeKind(rng: Rng): PrizeKind {
  return rng.int(0, PRIZE_KIND_COUNT - 1) as PrizeKind;
}

export function spawnPrize(ctx: GameContext, x: number, y: number, kind: PrizeKind): number {
  if (!ctx.pools.tryAcquire(PoolKind.Pickup)) return NO_ENTITY;
  const eid = baseBody(ctx, x, y, Config.pickups.weaponRadius, PRIZE_SPRITES[kind], 1.2, 0xffd94a, 0.2);
  addComponent(ctx.world, eid, Pickup);
  addComponent(ctx.world, eid, Lifetime);
  Pickup.kind[eid] = PickupKind.Prize;
  Pickup.sub[eid] = kind;
  Pickup.drift[eid] = 0;
  Lifetime.t[eid] = Config.pickups.prizeLife;
  Sprite.pop[eid] = 1;
  return eid;
}

export function spawnShieldPickup(ctx: GameContext, x: number, y: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Pickup)) return NO_ENTITY;
  const eid = baseBody(ctx, x, y, Config.pickups.weaponRadius, SpriteId.PickupShield, 1.45, 0xffffff, 0.22);
  addComponent(ctx.world, eid, Pickup);
  addComponent(ctx.world, eid, Lifetime);
  Pickup.kind[eid] = PickupKind.Shield;
  Pickup.sub[eid] = 0;
  Pickup.drift[eid] = 0;
  Lifetime.t[eid] = Config.pickups.weaponLife;
  Sprite.pop[eid] = 1;
  return eid;
}

export function spawnSwiftPickup(ctx: GameContext, x: number, y: number): number {
  if (!ctx.pools.tryAcquire(PoolKind.Pickup)) return NO_ENTITY;
  const c = Config.pickups.swift;
  const eid = baseBody(ctx, x, y, Config.pickups.weaponRadius * 0.95, SpriteId.PrizePotion, 1.25, c.pickupTint, 0.22);
  addComponent(ctx.world, eid, Pickup);
  addComponent(ctx.world, eid, Lifetime);
  Pickup.kind[eid] = PickupKind.Swift;
  Pickup.sub[eid] = 0;
  Pickup.drift[eid] = 0;
  Lifetime.t[eid] = Config.pickups.weaponLife;
  Sprite.pop[eid] = 1;
  return eid;
}

/** Random mid-fight / loot bonus: ward or gale. */
export function spawnBonusPickup(ctx: GameContext, x: number, y: number): number {
  return ctx.rng.chance(0.55) ? spawnShieldPickup(ctx, x, y) : spawnSwiftPickup(ctx, x, y);
}

export function giveSwift(eid: number): void {
  Player.swiftTime[eid] = Config.pickups.swift.duration;
  Sprite.pop[eid] = Math.max(Sprite.pop[eid], 0.4);
  Sprite.flash[eid] = Math.max(Sprite.flash[eid], 0.55);
}

export function giveShield(eid: number): void {
  Player.offhand[eid] = OFFHAND_SHIELD;
  Player.offhandTime[eid] = Config.pickups.shield.duration;
  Sprite.pop[eid] = Math.max(Sprite.pop[eid], 0.55);
  Sprite.flash[eid] = Math.max(Sprite.flash[eid], 0.7);
}

export function stripOffhand(eid: number): void {
  Player.offhand[eid] = OFFHAND_NONE;
  Player.offhandTime[eid] = 0;
}

export function spawnOrbiter(ctx: GameContext, owner: number, angle: number): number {
  const c = Config.pickups.shield;
  const x = Position.x[owner] + Math.cos(angle) * c.orbitRadius;
  const y = Position.y[owner] + Math.sin(angle) * c.orbitRadius;
  const eid = baseBody(ctx, x, y, c.bladeRadius, SpriteId.WardBlade, 0.72, 0xffffff, 0.78);
  addComponent(ctx.world, eid, Orbiter);
  addComponent(ctx.world, eid, TeamTag);
  addComponent(ctx.world, eid, Damage);
  addComponent(ctx.world, eid, Knockback);
  TeamTag.team[eid] = Team.Player;
  Orbiter.owner[eid] = owner;
  Orbiter.angle[eid] = angle;
  Orbiter.radius[eid] = c.orbitRadius;
  Orbiter.spin[eid] = c.spin;
  Orbiter.cool[eid] = 0;
  Damage.amount[eid] = c.damage;
  Damage.spawnerMult[eid] = c.spawnerMult;
  Knockback.force[eid] = c.knockback;
  Sprite.alpha[eid] = 0.95;
  Sprite.pop[eid] = 1;
  return eid;
}

export function killOrbiters(ctx: GameContext, owner: number): void {
  const blades = query(ctx.world, [Orbiter]);
  for (let i = 0; i < blades.length; i++) {
    const b = blades[i];
    if (Orbiter.owner[b] === owner && !Dying.flag[b]) kill(b);
  }
}

/** Mark for removal at the end of the tick so systems iterating queries stay stable. */
export function kill(eid: number): void {
  Dying.flag[eid] = DYING_REAP;
}

/**
 * Start the death clip and reap when it finishes. The entity stops interacting
 * immediately (collision and AI skip anything with Dying.flag set).
 */
export function startDeath(ctx: GameContext, eid: number): void {
  if (Dying.flag[eid]) return;
  Dying.flag[eid] = DYING_ANIM;
  playClip(eid, ClipId.Death, true, true);
  Anim.speed[eid] = 1;
  if (!hasComponent(ctx.world, eid, Lifetime)) addComponent(ctx.world, eid, Lifetime);
  Lifetime.t[eid] = Anims.duration(Sprite.id[eid] as SpriteId, ClipId.Death);
  Velocity.x[eid] *= 0.3;
  Velocity.y[eid] *= 0.3;
  Sprite.flash[eid] = Math.max(Sprite.flash[eid], 0.8);
  Sprite.pop[eid] = Math.max(Sprite.pop[eid], 0.22);
}

/** Actually removes the entity and returns its pool slot. Called by LifetimeSystem only. */
export function destroyEntity(ctx: GameContext, eid: number): void {
  const w = ctx.world;
  if (hasComponent(w, eid, Enemy)) {
    ctx.pools.release(enemyPool(Enemy.kind[eid] as EnemyKind));
  } else if (hasComponent(w, eid, Projectile)) {
    ctx.pools.release(PoolKind.Projectile);
  } else if (hasComponent(w, eid, Pickup)) {
    ctx.pools.release(PoolKind.Pickup);
  } else if (hasComponent(w, eid, Spawner)) {
    ctx.pools.release(PoolKind.Spawner);
  } else if (hasComponent(w, eid, FxTag)) {
    ctx.pools.release(PoolKind.Fx);
  }
  Dying.flag[eid] = 0;
  removeEntity(w, eid);
}
